import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { WIDGET_CUSTOMER_PRESENCE_USER_ID } from "@redotech/effect-api-shared/models/team/presence-notification";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo } from "react";
import type { SupportTicketPresenceRecord } from "../db/collections";

const DEFAULT_MAX_VISIBLE_AVATARS = 3;

interface KnownPresenceEntry {
  readonly record: SupportTicketPresenceRecord;
  readonly user: AssignableUser;
}

export const PresenceAvatarGroup = memo(function PresenceAvatarGroup({
  users,
  userMap,
  maxAvatars = DEFAULT_MAX_VISIBLE_AVATARS,
}: {
  users: SupportTicketPresenceRecord[];
  userMap: Map<MongoUserId, AssignableUser>;
  maxAvatars?: number;
}) {
  const overlap = 16;
  // Skip records whose userId isn't in the accessible-user map. Those
  // are users we genuinely don't have a name for (different company,
  // deleted, etc.) and a raw id avatar is more confusing than no
  // indicator at all.
  const knownUsers: KnownPresenceEntry[] = useMemo(
    () =>
      users.flatMap((record) => {
        // The widget-customer sentinel ("Customer") isn't a merchant user;
        // it never resolves into the userMap and would just clutter the
        // group, so skip it explicitly.
        if (record.userId === WIDGET_CUSTOMER_PRESENCE_USER_ID) {
          return [];
        }
        const user = userMap.get(record.userId);
        return user ? [{ record, user }] : [];
      }),
    [users, userMap],
  );
  const sortedUsers = useMemo(
    () =>
      [...knownUsers].sort((a, b) => {
        if (a.record.isTyping !== b.record.isTyping) {
          return a.record.isTyping ? -1 : 1;
        }
        return a.user.name.localeCompare(b.user.name);
      }),
    [knownUsers],
  );
  const visible = sortedUsers.slice(0, maxAvatars);
  const overflow = sortedUsers.length - visible.length;

  if (sortedUsers.length === 0) {
    return null;
  }

  return (
    <Flex align="center">
      <Flex position="relative">
        {visible.map(({ record, user }, index) => {
          const label = record.isTyping
            ? `${user.name} is typing…`
            : `${user.name} is viewing`;

          return (
            <Tooltip arrow key={record.id} title={label}>
              <div
                style={{
                  marginLeft: index === 0 ? 0 : -overlap,
                  position: "relative",
                  zIndex: maxAvatars - index,
                }}
              >
                <RedoAvatar
                  content={{ type: "text", text: user.name }}
                  size="xs"
                  status={record.isTyping ? { type: "typing" } : undefined}
                />
              </div>
            </Tooltip>
          );
        })}
      </Flex>
      {overflow > 0 && (
        <Text fontSize="xxs" ml="xs" textColor="tertiary">
          +{overflow}
        </Text>
      )}
    </Flex>
  );
});
